import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import DownloadIcon from '@mui/icons-material/Download';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import PageLayout from '../components/common/PageLayout';
import PdfFitPreview from '../components/common/PdfFitPreview';
import FinanceDraftInvoiceEditor from '../components/finance/FinanceDraftInvoiceEditor';
import { fetchFinanceDraftInvoicePdfBlob } from '../services/api';
import { downloadBlob } from '../utils/downloadBlob';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import useMobileView from '../hooks/useMobileView';

const ACCENT = '#4ce1b6';

export default function DraftInvoicePage() {
  const { isCompact } = useMobileView();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const [pdfUrl, setPdfUrl] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [previewError, setPreviewError] = useState(null);
  const lastPayloadRef = useRef('');
  const pdfUrlRef = useRef(null);

  useEffect(() => {
    return watchThemeChanges(setCurrentTheme);
  }, []);

  useEffect(() => () => {
    if (pdfUrlRef.current) {
      URL.revokeObjectURL(pdfUrlRef.current);
    }
  }, []);

  const handleGenerateDraft = useCallback(async (payload, options = {}) => {
    const serialized = JSON.stringify(payload);
    if (!options.force && serialized === lastPayloadRef.current) return;
    lastPayloadRef.current = serialized;
    setGenerating(true);
    setPreviewError(null);
    try {
      const blob = await fetchFinanceDraftInvoicePdfBlob(payload);
      const nextUrl = URL.createObjectURL(blob);
      const previousUrl = pdfUrlRef.current;
      pdfUrlRef.current = nextUrl;
      setPdfUrl(nextUrl);
      if (previousUrl) {
        window.setTimeout(() => URL.revokeObjectURL(previousUrl), 3000);
      }
    } catch (err) {
      setPreviewError(err?.response?.data?.error || err?.message || 'Failed to generate PDF preview.');
    } finally {
      setGenerating(false);
    }
  }, []);

  const handleDownload = async () => {
    if (!pdfUrl) return;
    try {
      const response = await fetch(pdfUrl);
      const blob = await response.blob();
      await downloadBlob(blob, 'DraftInvoice.pdf');
    } catch (err) {
      setPreviewError(err?.message || 'Failed to download PDF.');
    }
  };

  const previewPaperSx = {
    borderRadius: '18px',
    overflow: 'hidden',
    border: `1px solid ${alpha(ACCENT, isDark ? 0.22 : 0.2)}`,
    background: isDark
      ? 'linear-gradient(155deg, rgba(0,0,0,0.72) 0%, rgba(3,7,18,0.92) 100%)'
      : 'linear-gradient(155deg, #ffffff 0%, #f8fafc 100%)',
    boxShadow: isDark
      ? `0 24px 48px rgba(0,0,0,0.35), 0 0 0 1px ${alpha('#ffffff', 0.04)}`
      : '0 20px 40px rgba(15,23,42,0.08)',
  };

  const editorPaperSx = {
    ...previewPaperSx,
    p: { xs: 1.5, sm: 2, md: 2.5 },
  };

  const emptyPreviewMinHeight = { xs: 420, sm: 520, md: 560 };

  return (
    <PageLayout
      title="Draft Invoice"
      showFinanceSectionTabs
      showCard={false}
      showBreadcrumbs={false}
      navLinks={[
        { url: '/finance/reports/', label: 'Reports' },
        { url: '/finance/rates/', label: 'Manage Rate' },
      ]}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', lg: 'row' },
          alignItems: { lg: 'stretch' },
          gap: isCompact ? 2 : 2.5,
          width: '100%',
          minWidth: 0,
        }}
      >
        <Box sx={{ width: { xs: '100%', lg: '41.666%' }, flexShrink: 0, minWidth: 0 }}>
          <Paper elevation={0} sx={editorPaperSx}>
            <Stack spacing={0.5} sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={800} sx={{ letterSpacing: -0.3 }}>
                Invoice builder
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Pick room types, adjust rates and taxes — preview updates automatically.
              </Typography>
            </Stack>
            <FinanceDraftInvoiceEditor
              accentColor={ACCENT}
              onGenerateDraft={handleGenerateDraft}
              generating={generating}
            />
          </Paper>
        </Box>

        <Box
          sx={{
            width: { xs: '100%', lg: '58.334%' },
            flex: { lg: '1 1 auto' },
            minWidth: 0,
            alignSelf: { lg: 'flex-start' },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              ...previewPaperSx,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              spacing={1}
              sx={{
                px: 2,
                py: 1.25,
                flexShrink: 0,
                borderBottom: `1px solid ${alpha(ACCENT, isDark ? 0.18 : 0.14)}`,
                background: isDark ? alpha('#ffffff', 0.03) : alpha(ACCENT, 0.06),
              }}
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#042018',
                    background: 'linear-gradient(180deg, #58f2ce 0%, #44e8bf 100%)',
                  }}
                >
                  <PictureAsPdfIcon fontSize="small" />
                </Box>
                <Box>
                  <Typography variant="subtitle2" fontWeight={800}>Live PDF preview</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {generating && pdfUrl
                      ? 'Updating preview…'
                      : 'Draft — not saved to records'}
                  </Typography>
                </Box>
              </Stack>
              <Button
                size="small"
                variant="outlined"
                startIcon={<DownloadIcon />}
                disabled={!pdfUrl || generating}
                onClick={handleDownload}
                sx={{
                  borderRadius: '10px',
                  textTransform: 'none',
                  fontWeight: 700,
                  borderColor: alpha(ACCENT, 0.45),
                  color: isDark ? '#f8fafc' : '#0f172a',
                }}
              >
                Download
              </Button>
            </Stack>

            <Box
              sx={{
                position: 'relative',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                bgcolor: isDark ? '#0b0f17' : '#e8edf3',
                backgroundImage: isDark
                  ? `radial-gradient(circle at 20% 20%, ${alpha(ACCENT, 0.08)} 0%, transparent 45%)`
                  : `radial-gradient(circle at 80% 0%, ${alpha(ACCENT, 0.12)} 0%, transparent 40%)`,
              }}
            >
              {generating && !pdfUrl ? (
                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: emptyPreviewMinHeight, gap: 1.5 }}>
                  <CircularProgress size={36} sx={{ color: ACCENT }} />
                  <Typography variant="body2" color="text.secondary">Generating preview…</Typography>
                </Stack>
              ) : previewError ? (
                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: emptyPreviewMinHeight, px: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="error">{previewError}</Typography>
                </Stack>
              ) : pdfUrl ? (
                <Box
                  sx={{
                    opacity: generating ? 0.92 : 1,
                    transition: 'opacity 180ms ease',
                  }}
                >
                  <PdfFitPreview pdfUrl={pdfUrl} accentColor={ACCENT} />
                </Box>
              ) : (
                <Stack alignItems="center" justifyContent="center" sx={{ minHeight: emptyPreviewMinHeight, px: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Adjust line items to generate your draft invoice preview.
                  </Typography>
                </Stack>
              )}
            </Box>
          </Paper>
        </Box>
      </Box>
    </PageLayout>
  );
}
